/**
 * SafeRouterProvider - Wraps RouterProvider with error boundary
 */

import { Component, type ComponentProps, type ReactNode } from 'react';
import { RouterProvider } from 'react-router';

interface Props {
  router: ComponentProps<typeof RouterProvider>['router'];
  children?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class SafeRouterProvider extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('🚨 SafeRouterProvider caught error:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-900 p-4">
          <div className="max-w-md w-full bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-neutral-400 mb-6">
              The app encountered an error. Please try reloading.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return <RouterProvider router={this.props.router} />;
  }
}
